import {
  SCENARIO_KIND_LABELS,
  formatDate,
  formatDecimal,
  scenarioBaseResultSchema,
  scenarioComparisonSchema,
  scenarioHistorySchema,
  scenarioInputSchema,
  scenarioListSchema,
  scenarioOptionsSchema,
  scenarioPremisesInputSchema,
  scenarioSchema,
  scenarioTargetsSchema,
  type Scenario,
  type ScenarioComparison,
  type ScenarioOptions,
} from '@fluxus/shared';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Archive,
  ArchiveRestore,
  ArrowDown,
  ArrowUp,
  Crown,
  Eye,
  FolderKanban,
  Layers3,
  Lock,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { ProjectPicker, useCurrentProject } from '@/features/workspace/workspace';
import { ApiError, errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import { moneyInput } from '@/lib/money';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
const value = (v: number | null) => (v === null ? '—' : formatDecimal(v, 2));
const inputText = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : v.toLocaleString('pt-BR', { maximumFractionDigits: 6 });
const textareaClass =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-2 focus-visible:outline-ring aria-invalid:border-danger';

/**
 * Planejamento › Cenários (T-180b, RF-28, RF-14, PRD §11.16; DEC-063): cenários do projeto com premissas e metas,
 * comparados com o realizado e a projeção atual; o cenário base vira as metas oficiais.
 */
export function CenariosPage() {
  const { organizationId = '' } = useParams();
  const user = useCurrentUser();
  const { workspace, projects, project, select } = useCurrentProject(organizationId);
  return (
    <PageContainer className="max-w-5xl">
      <PageHeader
        title="Cenários"
        description="Pessimista, realista, otimista e outros: premissas e metas de cada um, comparados com o realizado e a projeção. O cenário base define as metas oficiais."
      />
      {workspace.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando o projeto" />
      ) : workspace.isError ? (
        <ErrorState
          className="mt-6"
          error={workspace.error}
          onRetry={() => void workspace.refetch()}
          retrying={workspace.isFetching}
        />
      ) : !project ? (
        <EmptyState
          className="mt-6 rounded-lg border border-dashed border-border py-12"
          icon={FolderKanban}
          title="Nenhum projeto BSC em andamento"
          description="Os cenários pertencem a um projeto. Quando a empresa tiver um projeto em andamento, eles aparecem aqui."
          action={
            user.organization.isFluxus ? (
              <Button asChild variant="outline">
                <Link to={`/empresas/${organizationId}`}>Abrir um projeto na página da empresa</Link>
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div className="mt-4">
            <ProjectPicker projects={projects} project={project} onSelect={select} />
          </div>
          <ScenariosSection
            key={project.id}
            organizationId={organizationId}
            projectId={project.id}
            readOnly={project.status !== 'ACTIVE'}
          />
        </>
      )}
    </PageContainer>
  );
}

function ScenariosSection({
  organizationId,
  projectId,
  readOnly,
}: {
  organizationId: string;
  projectId: string;
  readOnly: boolean;
}) {
  const { client } = useAuth();
  const canDo = useCan();
  const can: typeof canDo = (module, action) => !readOnly && canDo(module, action);
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/scenarios`;
  const [params, setParams] = useSearchParams();
  const [archived, setArchived] = useState(false);
  const [editing, setEditing] = useState<Scenario | 'new' | null>(null);
  const [choosing, setChoosing] = useState<Scenario | null>(null);
  const openId = params.get('cenario');
  const list = useQuery({
    queryKey: ['scenarios', projectId, 'list', archived],
    queryFn: ({ signal }) =>
      client.request(base, {
        query: archived ? { archived: 'true' } : {},
        schema: scenarioListSchema,
        signal,
      }),
    placeholderData: keepPreviousData,
  });
  const options = useQuery({
    queryKey: ['scenarios', projectId, 'options'],
    queryFn: ({ signal }) => client.request(`${base}/options`, { schema: scenarioOptionsSchema, signal }),
  });
  // A comparação e as metas oficiais (do base) mudam junto.
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['scenarios', projectId] }),
      queryClient.invalidateQueries({ queryKey: ['targets'] }),
    ]);
  const openScenario = (id: string | null) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('cenario', id);
        else next.delete('cenario');
        return next;
      },
      { replace: true },
    );
  const toggleArchive = useMutation({
    meta: { handlesError: true },
    mutationFn: (s: Scenario) =>
      client.request(`${base}/${s.id}/${s.archivedAt ? 'restore' : 'archive'}`, {
        method: 'POST',
        schema: scenarioSchema,
      }),
    onSuccess: async (s) => {
      toast.success(s.archivedAt ? 'Cenário arquivado' : 'Cenário restaurado', { description: s.name });
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível concluir', { description: errorMessage(err) }),
  });
  const canChooseBase = can('cenarios', 'edit') && canDo('metas-resultados', 'edit');
  const items = list.data?.items ?? [];
  const opened = items.find((s) => s.id === openId);

  return (
    <div className="mt-6 flex flex-col gap-8">
      {readOnly ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Projeto arquivado: os cenários ficam disponíveis só para consulta.
        </p>
      ) : null}
      <section aria-label="Cenários do projeto" className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-base font-bold">{archived ? 'Cenários arquivados' : 'Cenários'}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <input
                type="checkbox"
                className="size-4 accent-(--brand)"
                checked={archived}
                onChange={(e) => setArchived(e.target.checked)}
              />
              Ver só os arquivados
            </label>
            {can('cenarios', 'create') && !archived ? (
              <Button onClick={() => setEditing('new')}>
                <Plus aria-hidden />
                Novo cenário
              </Button>
            ) : null}
          </div>
        </div>
        {list.isPending ? (
          <LoadingState rows={3} label="Carregando os cenários" />
        ) : list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
        ) : items.length === 0 ? (
          <EmptyState
            className="rounded-lg border border-dashed border-border py-10"
            icon={Layers3}
            title={archived ? 'Nenhum cenário arquivado' : 'Nenhum cenário neste projeto'}
            description={
              archived
                ? 'Os cenários arquivados aparecem aqui e podem ser restaurados.'
                : 'Cadastre os cenários do planejamento: premissas e metas de cada um.'
            }
          />
        ) : (
          <ul aria-label="Cenários" className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((s) => (
              <li key={s.id} className="rounded-lg border border-border bg-surface p-4">
                <article aria-label={s.name} className="flex h-full flex-col gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-semibold break-words">{s.name}</h3>
                    {/* O tipo só aparece quando o nome não o diz (cenário inicial renomeado). */}
                    {s.kind !== 'CUSTOM' && s.name !== SCENARIO_KIND_LABELS[s.kind] ? (
                      <StatusBadge status="gray" label={SCENARIO_KIND_LABELS[s.kind]} />
                    ) : null}
                    {s.isBase ? <StatusBadge status="green" label="Cenário base" /> : null}
                  </div>
                  {s.description ? <p className="text-sm text-muted-foreground">{s.description}</p> : null}
                  <p className="text-xs text-muted-foreground">
                    {s.premises.length} {s.premises.length === 1 ? 'premissa' : 'premissas'} · {s.targetCount}{' '}
                    {s.targetCount === 1 ? 'meta' : 'metas'}
                    {s.isBase && s.baseSetAt
                      ? ` · base desde ${formatDate(s.baseSetAt.slice(0, 10))}${s.baseSetByName ? ` (${s.baseSetByName})` : ''}`
                      : ''}
                  </p>
                  <div className="mt-auto flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openScenario(s.id)}>
                      <Eye aria-hidden />
                      Abrir <span className="sr-only">{s.name}</span>
                    </Button>
                    {!s.archivedAt && canChooseBase ? (
                      <Button variant="outline" size="sm" onClick={() => setChoosing(s)}>
                        <Crown aria-hidden />
                        {s.isBase ? 'Reaplicar como base' : 'Tornar base'}{' '}
                        <span className="sr-only">{s.name}</span>
                      </Button>
                    ) : null}
                    {!s.archivedAt && can('cenarios', 'edit') ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Editar ${s.name}`}
                        onClick={() => setEditing(s)}
                      >
                        <Pencil aria-hidden />
                      </Button>
                    ) : null}
                    {can('cenarios', 'delete') && !s.isBase ? (
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`${s.archivedAt ? 'Restaurar' : 'Arquivar'} ${s.name}`}
                        disabled={toggleArchive.isPending}
                        onClick={() => toggleArchive.mutate(s)}
                      >
                        {s.archivedAt ? <ArchiveRestore aria-hidden /> : <Archive aria-hidden />}
                      </Button>
                    ) : null}
                  </div>
                </article>
              </li>
            ))}
          </ul>
        )}
      </section>

      <ComparisonSection base={base} projectId={projectId} options={options} />

      <ScenarioDialog
        base={base}
        projectId={projectId}
        scenario={opened}
        open={openId !== null}
        list={list}
        options={options}
        canEdit={can('cenarios', 'edit')}
        onClose={() => openScenario(null)}
        onChanged={refresh}
      />
      <Dialog
        open={editing !== null}
        onOpenChange={(open) => !open && setEditing(null)}
        title={editing === 'new' ? 'Novo cenário' : 'Editar cenário'}
      >
        {editing !== null ? (
          <ScenarioForm
            base={base}
            initial={editing === 'new' ? undefined : editing}
            onDone={async () => {
              setEditing(null);
              await refresh();
            }}
            onCancel={() => setEditing(null)}
          />
        ) : null}
      </Dialog>
      <BaseDialog
        base={base}
        scenario={choosing}
        onClose={() => setChoosing(null)}
        onDone={async () => {
          setChoosing(null);
          await refresh();
        }}
      />
    </div>
  );
}

function ScenarioForm({
  base,
  initial,
  onDone,
  onCancel,
}: {
  base: string;
  initial?: Scenario;
  onDone: () => Promise<void>;
  onCancel: () => void;
}) {
  const { client } = useAuth();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { name: useId(), description: useId() };
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const parsed = scenarioInputSchema.safeParse({ name, description });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setFormError(null);
    setSaving(true);
    try {
      const saved = await client.request(initial ? `${base}/${initial.id}` : base, {
        method: initial ? 'PUT' : 'POST',
        body: parsed.data,
        schema: scenarioSchema,
      });
      toast.success(initial ? 'Cenário salvo' : 'Cenário criado', { description: saved.name });
      await onDone();
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }
  return (
    <form noValidate onSubmit={submit} className="flex flex-col gap-4">
      <FormField id={ids.name} label="Nome do cenário" error={errors.name}>
        <Input
          {...fieldA11y(ids.name, errors.name)}
          value={name}
          maxLength={80}
          placeholder="Ex.: Expansão com duas lojas"
          onChange={(e) => setName(e.target.value)}
          className="h-11"
        />
      </FormField>
      <FormField id={ids.description} label="Descrição (opcional)" error={errors.description}>
        <textarea
          {...fieldA11y(ids.description, errors.description)}
          value={description}
          rows={3}
          maxLength={1000}
          onChange={(e) => setDescription(e.target.value)}
          className={textareaClass}
        />
      </FormField>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button type="submit" loading={saving}>
          {initial ? 'Salvar cenário' : 'Criar cenário'}
        </Button>
      </div>
    </form>
  );
}

/** Escolher (ou reaplicar) o cenário base: as metas dele viram as oficiais (DEC-063). */
function BaseDialog({
  base,
  scenario,
  onClose,
  onDone,
}: {
  base: string;
  scenario: Scenario | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const { client } = useAuth();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [needsReason, setNeedsReason] = useState(false);
  const [saving, setSaving] = useState(false);
  const id = useId();
  useEffect(() => {
    if (!scenario) return;
    setReason('');
    setError(null);
    setNeedsReason(false);
  }, [scenario]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!scenario || saving) return;
    setSaving(true);
    setError(null);
    try {
      const out = await client.request(`${base}/${scenario.id}/base`, {
        method: 'POST',
        body: { reason: reason.trim() || null },
        schema: scenarioBaseResultSchema,
      });
      const { created, revised, removed, pendingApproval } = out.applied;
      toast.success(`${out.scenario.name} é o cenário base`, {
        description: `Metas oficiais: ${created} criadas, ${revised} revisadas, ${removed} removidas.${
          pendingApproval ? ' Ficaram pendentes de aprovação.' : ''
        }`,
      });
      await onDone();
    } catch (err) {
      // Mudar metas já definidas (ou de mês fechado) exige justificativa: a tela pede.
      if (err instanceof ApiError && (err.code === 'REASON_REQUIRED' || err.code === 'CLOSED_PERIOD')) {
        setNeedsReason(true);
      }
      setError(err);
    } finally {
      setSaving(false);
    }
  }
  return (
    <Dialog
      open={scenario !== null}
      onOpenChange={(open) => !open && onClose()}
      busy={saving}
      title={scenario?.isBase ? 'Reaplicar o cenário base' : 'Tornar cenário base'}
      description={
        scenario
          ? `As metas de ${scenario.name} passam a ser as metas oficiais nos indicadores e anos que ele cobre (as que ele não tem saem).`
          : undefined
      }
    >
      <form noValidate onSubmit={submit} className="flex flex-col gap-4">
        <FormField
          id={id}
          label={needsReason ? 'Justificativa' : 'Justificativa (para metas que já existiam)'}
          error={needsReason && !reason.trim() ? 'Justificativa obrigatória.' : undefined}
        >
          <textarea
            {...fieldA11y(id, needsReason && !reason.trim() ? 'x' : undefined)}
            value={reason}
            rows={3}
            maxLength={500}
            placeholder="Por que este cenário passa a valer (ex.: aprovado pela diretoria)"
            onChange={(e) => setReason(e.target.value)}
            className={textareaClass}
          />
        </FormField>
        {error ? <FormAlert tone="error">{errorMessage(error)}</FormAlert> : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving}>
            <Crown aria-hidden />
            {scenario?.isBase ? 'Reaplicar metas' : 'Tornar base'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

type OptionsQuery = ReturnType<typeof useQuery<ScenarioOptions>>;

function ScenarioDialog({
  base,
  projectId,
  scenario,
  open,
  list,
  options,
  canEdit,
  onClose,
  onChanged,
}: {
  base: string;
  projectId: string;
  scenario: Scenario | undefined;
  open: boolean;
  list: { isPending: boolean; isError: boolean; error: unknown; isFetching: boolean; refetch: () => unknown };
  options: OptionsQuery;
  canEdit: boolean;
  onClose: () => void;
  onChanged: () => Promise<unknown>;
}) {
  const [tab, setTab] = useState<'premises' | 'targets' | 'history'>('premises');
  const tabsId = useId();
  const editable = canEdit && Boolean(scenario) && !scenario?.archivedAt;
  const tabs = [
    { key: 'premises' as const, label: 'Premissas' },
    { key: 'targets' as const, label: 'Metas' },
    { key: 'history' as const, label: 'Histórico' },
  ];
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={scenario?.name ?? 'Cenário'}
      description={scenario?.isBase ? 'Cenário base: as metas dele são as metas oficiais.' : undefined}
      className="w-[min(52rem,calc(100vw-2rem))]"
    >
      {list.isPending ? (
        <LoadingState rows={3} label="Carregando o cenário" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : !scenario ? (
        <EmptyState
          icon={Layers3}
          title="Cenário não encontrado"
          description="Ele foi arquivado ou não existe neste projeto."
        />
      ) : (
        <div className="flex flex-col gap-4 text-sm">
          <div
            role="tablist"
            aria-label="Seções do cenário"
            className="flex gap-1 overflow-x-auto border-b border-border"
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                id={`${tabsId}-tab-${t.key}`}
                aria-selected={tab === t.key}
                aria-controls={`${tabsId}-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`-mb-px min-h-11 shrink-0 border-b-2 px-3 text-sm font-medium ${
                  tab === t.key ? 'border-brand text-foreground' : 'border-transparent text-muted-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div role="tabpanel" id={`${tabsId}-${tab}`} aria-labelledby={`${tabsId}-tab-${tab}`}>
            {tab === 'premises' ? (
              <PremisesEditor base={base} scenario={scenario} editable={editable} onChanged={onChanged} />
            ) : tab === 'targets' ? (
              <TargetsEditor
                base={base}
                projectId={projectId}
                scenario={scenario}
                options={options}
                editable={editable && !scenario.isBase}
                onChanged={onChanged}
              />
            ) : (
              <History base={base} projectId={projectId} scenario={scenario} />
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
}

function PremisesEditor({
  base,
  scenario,
  editable,
  onChanged,
}: {
  base: string;
  scenario: Scenario;
  editable: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const [rows, setRows] = useState<{ key: string; id?: string; text: string }[]>(() =>
    scenario.premises.map((p) => ({ key: p.id, id: p.id, text: p.text })),
  );
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  useEffect(
    () => setRows(scenario.premises.map((p) => ({ key: p.id, id: p.id, text: p.text }))),
    [scenario.premises],
  );
  if (!editable) {
    return scenario.premises.length ? (
      <ol className="flex list-decimal flex-col gap-1 pl-5">
        {scenario.premises.map((p) => (
          <li key={p.id}>{p.text}</li>
        ))}
      </ol>
    ) : (
      <p className="text-muted-foreground">Nenhuma premissa cadastrada.</p>
    );
  }
  const move = (i: number, d: number) =>
    setRows((list) => {
      const next = [...list];
      const [row] = next.splice(i, 1);
      next.splice(i + d, 0, row!);
      return next;
    });
  async function save(e: FormEvent) {
    e.preventDefault();
    if (saving) return;
    const body = { items: rows.map((r) => ({ ...(r.id ? { id: r.id } : {}), text: r.text })) };
    const parsed = scenarioPremisesInputSchema.safeParse(body);
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setFormError(null);
    setSaving(true);
    try {
      await client.request(`${base}/${scenario.id}/premises`, {
        method: 'PUT',
        body: parsed.data,
        schema: scenarioSchema,
      });
      toast.success('Premissas salvas', { description: scenario.name });
      await onChanged();
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }
  return (
    <form noValidate onSubmit={save} className="flex flex-col gap-3">
      {rows.length === 0 ? <p className="text-muted-foreground">Nenhuma premissa ainda.</p> : null}
      <ol aria-label="Premissas" className="flex flex-col gap-2">
        {rows.map((r, i) => (
          <li key={r.key} className="flex items-start gap-2">
            <span className="mt-3 w-5 shrink-0 text-right text-xs text-muted-foreground tabular">
              {i + 1}.
            </span>
            <div className="flex-1">
              <label htmlFor={`premise-${r.key}`} className="sr-only">
                Premissa {i + 1}
              </label>
              <Input
                id={`premise-${r.key}`}
                value={r.text}
                maxLength={500}
                aria-invalid={Boolean(errors[`items.${i}.text`])}
                onChange={(e) =>
                  setRows((list) => list.map((x, k) => (k === i ? { ...x, text: e.target.value } : x)))
                }
                className="h-11"
              />
              {errors[`items.${i}.text`] ? (
                <p className="mt-1 text-xs text-status-red-fg">{errors[`items.${i}.text`]}</p>
              ) : null}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Subir a premissa ${i + 1}`}
              disabled={i === 0}
              onClick={() => move(i, -1)}
            >
              <ArrowUp aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Descer a premissa ${i + 1}`}
              disabled={i === rows.length - 1}
              onClick={() => move(i, 1)}
            >
              <ArrowDown aria-hidden />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={`Remover a premissa ${i + 1}`}
              onClick={() => setRows((list) => list.filter((_, k) => k !== i))}
            >
              <Trash2 aria-hidden />
            </Button>
          </li>
        ))}
      </ol>
      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => setRows((list) => [...list, { key: crypto.randomUUID(), text: '' }])}
        >
          <Plus aria-hidden />
          Adicionar premissa
        </Button>
        <Button type="submit" loading={saving}>
          Salvar premissas
        </Button>
      </div>
    </form>
  );
}

function TargetsEditor({
  base,
  projectId,
  scenario,
  options: optionsQuery,
  editable,
  onChanged,
}: {
  base: string;
  projectId: string;
  scenario: Scenario;
  options: OptionsQuery;
  editable: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const { client } = useAuth();
  const ids = { year: useId(), kpi: useId() };
  const options = optionsQuery.data;
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState<number | null>(null);
  const [kpiId, setKpiId] = useState('');
  const years = options?.years ?? [];
  const chosenYear = year ?? (years.includes(thisYear) ? thisYear : (years[0] ?? thisYear));
  const chosenKpi = kpiId || options?.kpis[0]?.id || '';
  const kpi = options?.kpis.find((k) => k.id === chosenKpi);
  const targets = useQuery({
    queryKey: ['scenarios', projectId, 'targets', scenario.id, chosenYear],
    queryFn: ({ signal }) =>
      client.request(`${base}/${scenario.id}/targets`, {
        query: { year: chosenYear },
        schema: scenarioTargetsSchema,
        signal,
      }),
    enabled: Boolean(options),
  });
  const [draft, setDraft] = useState<string[]>(Array(13).fill(''));
  // De qual KPI e ano é o rascunho: só se salva o rascunho carregado das metas do que está na tela (salvar um
  // rascunho vazio, com as metas carregando ou em erro, removeria as metas).
  const [draftFor, setDraftFor] = useState<string | null>(null);
  const loadedKey = targets.isSuccess ? `${chosenKpi}|${chosenYear}` : null;
  const ready = loadedKey !== null && draftFor === loadedKey;
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setDraftFor(loadedKey);
    const cells = (targets.data?.cells ?? []).filter((c) => c.kpiId === chosenKpi);
    setDraft([
      ...Array.from({ length: 12 }, (_, m) =>
        inputText(cells.find((c) => c.periodType === 'MONTH' && c.period === m + 1)?.value),
      ),
      inputText(cells.find((c) => c.periodType === 'YEAR')?.value),
    ]);
    setError(null);
  }, [targets.data, chosenKpi, loadedKey]);
  if (optionsQuery.isError) {
    return (
      <ErrorState
        error={optionsQuery.error}
        onRetry={() => void optionsQuery.refetch()}
        retrying={optionsQuery.isFetching}
      />
    );
  }
  if (!options) return <LoadingState rows={2} label="Carregando os indicadores" />;
  if (!options.kpis.length) {
    return <p className="text-muted-foreground">Nenhum indicador ativo no seu escopo neste projeto.</p>;
  }
  async function save(e: FormEvent) {
    e.preventDefault();
    if (saving || !ready) return;
    const parsed = draft.map((t) => (t.trim() === '' ? null : Number(moneyInput(t))));
    const bad = parsed.findIndex((v) => v !== null && !Number.isFinite(v));
    if (bad >= 0) {
      setError(`Valor inválido em ${bad === 12 ? 'Ano' : MONTHS[bad]}: use números (ex.: 2.500.000,50).`);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await client.request(`${base}/${scenario.id}/targets`, {
        method: 'PUT',
        body: {
          year: chosenYear,
          cells: parsed.map((v, i) => ({
            kpiId: chosenKpi,
            periodType: i === 12 ? 'YEAR' : 'MONTH',
            period: i === 12 ? 1 : i + 1,
            value: v,
          })),
        },
        schema: scenarioTargetsSchema,
      });
      toast.success('Metas do cenário salvas', { description: `${kpi?.code} · ${chosenYear}` });
      await onChanged();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }
  return (
    <form noValidate onSubmit={save} className="flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
        <FormField id={ids.year} label="Ano">
          <NativeSelect id={ids.year} value={chosenYear} onChange={(e) => setYear(Number(e.target.value))}>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.kpi} label="Indicador">
          <NativeSelect id={ids.kpi} value={chosenKpi} onChange={(e) => setKpiId(e.target.value)}>
            {options.kpis.map((k) => (
              <option key={k.id} value={k.id}>
                {k.code} · {k.name}
              </option>
            ))}
          </NativeSelect>
        </FormField>
      </div>
      {scenario.isBase ? (
        <FormAlert tone="locked">
          As metas do cenário base são as metas oficiais: altere-as em Metas e Resultados, ou escolha outro
          cenário como base.
        </FormAlert>
      ) : null}
      {targets.isPending ? (
        <LoadingState rows={2} label="Carregando as metas do cenário" />
      ) : targets.isError ? (
        <ErrorState
          error={targets.error}
          onRetry={() => void targets.refetch()}
          retrying={targets.isFetching}
        />
      ) : (
        <fieldset
          disabled={!editable || saving}
          className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6"
        >
          <legend className="sr-only">
            Metas de {kpi?.name} em {chosenYear} ({kpi?.unitLabel})
          </legend>
          {[...MONTHS, 'Ano'].map((label, i) => (
            <div key={label} className="flex flex-col gap-1">
              <label htmlFor={`${ids.kpi}-cell-${i}`} className="text-xs font-medium text-muted-foreground">
                {i === 12 ? `Ano ${chosenYear}` : `${label}/${chosenYear}`}
              </label>
              <Input
                id={`${ids.kpi}-cell-${i}`}
                inputMode="decimal"
                value={draft[i] ?? ''}
                onChange={(e) => setDraft((d) => d.map((v, k) => (k === i ? e.target.value : v)))}
                className={`h-11 text-right tabular ${i === 12 ? 'font-semibold' : ''}`}
              />
            </div>
          ))}
        </fieldset>
      )}
      <p className="text-xs text-muted-foreground">
        Valor anual: a meta do ano, se houver; senão, o acumulado das mensais pela agregação do indicador (
        {kpi?.unitLabel}). Deixe em branco para não ter meta.
      </p>
      {error ? <FormAlert tone="error">{error}</FormAlert> : null}
      {editable && ready ? (
        <div className="flex justify-end">
          <Button type="submit" loading={saving}>
            Salvar metas
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function History({ base, projectId, scenario }: { base: string; projectId: string; scenario: Scenario }) {
  const { client } = useAuth();
  const history = useInfiniteQuery({
    queryKey: ['scenarios', projectId, 'history', scenario.id],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      client.request(`${base}/${scenario.id}/history`, {
        query: pageParam ? { before: pageParam } : {},
        schema: scenarioHistorySchema,
        signal,
      }),
    getNextPageParam: (last) => last.nextCursor,
  });
  if (history.isPending) return <LoadingState rows={3} label="Carregando o histórico" />;
  if (history.isError) {
    return (
      <ErrorState
        error={history.error}
        onRetry={() => void history.refetch()}
        retrying={history.isFetching}
      />
    );
  }
  const items = history.data.pages.flatMap((p) => p.items);
  if (!items.length) return <p className="text-muted-foreground">Nenhuma revisão registrada ainda.</p>;
  return (
    <div className="flex flex-col gap-3">
      <ol aria-label="Histórico de revisões" className="flex flex-col gap-2">
        {items.map((h, i) => (
          <li key={`${h.at}-${i}`} className="rounded-md border border-border p-3">
            <p className="font-medium break-words">{h.summary}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {new Date(h.at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
              {h.actorName ? ` · ${h.actorName}` : ''}
            </p>
          </li>
        ))}
      </ol>
      {history.hasNextPage ? (
        <Button
          variant="outline"
          className="self-start"
          loading={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          Carregar mais
        </Button>
      ) : null}
    </div>
  );
}

function ComparisonSection({
  base,
  projectId,
  options,
}: {
  base: string;
  projectId: string;
  options: OptionsQuery;
}) {
  const { client } = useAuth();
  const ids = { year: useId(), kpi: useId() };
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState<number | null>(null);
  const [kpiId, setKpiId] = useState('');
  const years = options.data?.years ?? [];
  const chosenYear = year ?? (years.includes(thisYear) ? thisYear : (years[0] ?? thisYear));
  const chosenKpi = kpiId || options.data?.kpis[0]?.id || '';
  const comparison = useQuery({
    queryKey: ['scenarios', projectId, 'comparison', chosenKpi, chosenYear],
    queryFn: ({ signal }) =>
      client.request(`${base}/comparison`, {
        query: { kpiId: chosenKpi, year: chosenYear },
        schema: scenarioComparisonSchema,
        signal,
      }),
    enabled: Boolean(chosenKpi),
    placeholderData: keepPreviousData,
  });
  return (
    <section aria-label="Comparação entre cenários" className="flex flex-col gap-3">
      <h2 className="text-base font-bold">Comparação</h2>
      {options.isPending ? (
        <LoadingState rows={2} label="Carregando os indicadores" />
      ) : options.isError ? (
        <ErrorState
          error={options.error}
          onRetry={() => void options.refetch()}
          retrying={options.isFetching}
        />
      ) : !options.data.kpis.length ? (
        <EmptyState
          className="rounded-lg border border-dashed border-border py-8"
          icon={Layers3}
          title="Nenhum indicador para comparar"
          description="Cadastre os indicadores do projeto: a comparação mostra as metas de cada cenário, o realizado e a projeção."
        />
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
            <FormField id={ids.year} label="Ano">
              <NativeSelect
                id={ids.year}
                value={chosenYear}
                onChange={(e) => setYear(Number(e.target.value))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
            <FormField id={ids.kpi} label="Indicador">
              <NativeSelect id={ids.kpi} value={chosenKpi} onChange={(e) => setKpiId(e.target.value)}>
                {options.data.kpis.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.code} · {k.name}
                  </option>
                ))}
              </NativeSelect>
            </FormField>
          </div>
          {comparison.isPending ? (
            <LoadingState rows={3} label="Carregando a comparação" />
          ) : comparison.isError ? (
            <ErrorState
              error={comparison.error}
              onRetry={() => void comparison.refetch()}
              retrying={comparison.isFetching}
            />
          ) : (
            <ComparisonView data={comparison.data} />
          )}
        </>
      )}
    </section>
  );
}

/** Linhas da comparação: os cenários, o realizado e a projeção. */
function comparisonRows(c: ScenarioComparison) {
  return [
    ...c.scenarios.map((s) => ({
      key: s.id,
      name: `${s.name}${s.isBase ? ' (base)' : ''}`,
      annual: s.annual,
      months: s.months,
      tone: s.isBase ? 'base' : 'scenario',
    })),
    {
      key: 'actual',
      name: c.actual.lastActualMonth
        ? `Real / realizado (até ${MONTHS[c.actual.lastActualMonth - 1]})`
        : 'Real / realizado',
      annual: c.actual.annual,
      months: c.actual.months,
      tone: 'actual',
    },
    {
      key: 'projection',
      name: `Projeção atual${c.projection.method === 'RUN_RATE' ? ' (média)' : c.projection.method ? ' (tendência)' : ''}`,
      annual: c.projection.annual,
      months: c.projection.months,
      tone: 'projection',
    },
  ] as const;
}

const BAR_TONE: Record<string, string> = {
  scenario: 'bg-brand/60',
  base: 'bg-brand',
  actual: 'bg-status-green-solid',
  projection: 'bg-status-yellow-solid',
};

function ComparisonView({ data }: { data: ScenarioComparison }) {
  const rows = comparisonRows(data);
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.annual ?? 0)), Math.abs(data.officialAnnual ?? 0));
  return (
    <div className="flex flex-col gap-4">
      <figure className="flex flex-col gap-2">
        <figcaption className="text-sm font-medium">
          {data.kpi.code} · {data.kpi.name} — valor anual de {data.year} ({data.kpi.unitLabel})
          {data.officialAnnual !== null ? ` · meta oficial ${value(data.officialAnnual)}` : ''}
        </figcaption>
        <ul aria-label="Gráfico da comparação" className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.key} className="grid grid-cols-[minmax(7rem,12rem)_1fr] items-center gap-3 text-sm">
              <span className="truncate" title={r.name}>
                {r.name}
              </span>
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={`h-5 rounded-sm ${BAR_TONE[r.tone]}`}
                  style={{
                    width: `${r.annual === null ? 0 : Math.max(2, (Math.abs(r.annual) / max) * 100)}%`,
                  }}
                />
                <span className="shrink-0 font-medium tabular">{value(r.annual)}</span>
              </span>
            </li>
          ))}
        </ul>
      </figure>
      {/* Rola na horizontal em telas estreitas: a região recebe o foco para rolar pelo teclado. */}
      <div
        className="overflow-x-auto rounded-lg border border-border"
        role="region"
        aria-label={`Comparação de ${data.kpi.name} mês a mês`}
        tabIndex={0}
      >
        <table className="w-full min-w-[56rem] text-sm">
          <caption className="sr-only">
            Comparação de {data.kpi.name} em {data.year}: anual e mês a mês
          </caption>
          <thead className="bg-muted text-xs text-muted-foreground">
            <tr>
              <th scope="col" className="px-3 py-2 text-left">
                Cenário
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                Anual
              </th>
              {MONTHS.map((m) => (
                <th key={m} scope="col" className="px-2 py-2 text-right">
                  {m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-t border-border">
                <th scope="row" className="px-3 py-2 text-left font-medium">
                  {r.name}
                </th>
                <td className="px-3 py-2 text-right font-semibold tabular">{value(r.annual)}</td>
                {r.months.map((v, i) => (
                  <td key={i} className="px-2 py-2 text-right tabular">
                    {value(v)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
